/**
 * library.ts — Composições premium do Studio (Fase 8)
 *
 * Dez trechos de página inteiros, cada um com direção de arte própria e com os
 * espaços semânticos declarados (chapéu, título, texto, ação, visual, prova
 * social, lista, formulário). Três famílias:
 *
 *   • saas   — produto na tela, grade, brilho frio;
 *   • luxo   — fotografia, muito espaço, tipografia grande, pouco movimento;
 *   • evento — cor forte, movimento, pessoas.
 *
 * As composições também são publicadas na biblioteca lateral como blocos
 * prontos da categoria "Composições", reaproveitando integralmente a tela e a
 * inserção que já existem.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Dez composições iniciais (Studio, Fase 8)
 */

import { registerPattern, registerPatternCategory } from '../patterns/patternRegistry';
import { FOTO, AVATARES } from '../patterns/_shared';
import type { StudioOutput, StudioRenderMode, StudioModeSupport } from '../types';
import { expandComposition, registerCompositions } from './compositionRegistry';
import type { StudioCompositionDefinition, StudioCompositionNode } from './types';

/** Atalho para montar um nó de composição. */
function b(
  type: string,
  props: Record<string, unknown> = {},
  children?: StudioCompositionNode[],
): StudioCompositionNode {
  return children ? { type, props, children } : { type, props };
}

/** Atalho para montar um nó que ocupa um espaço semântico. */
function s(
  slotId: string,
  type: string,
  props: Record<string, unknown> = {},
  children?: StudioCompositionNode[],
): StudioCompositionNode {
  return { ...b(type, props, children), slotId };
}

const WEB: StudioOutput[] = ['web'];

/** Suporte padrão: completo na tela, estático em PDF/e-mail, seco na miniatura. */
const MODOS: Record<StudioRenderMode, StudioModeSupport> = {
  editor: 'full',
  published: 'full',
  thumbnail: 'static',
  pdf: 'static',
  email: 'static',
};

/** Espaços reutilizados por quase toda composição de abertura. */
const ESPACOS_DE_ABERTURA = [
  {
    id: 'chapeu',
    role: 'eyebrow' as const,
    label: 'Chapéu',
    acceptedTypes: ['ui.badge'],
    hint: 'Frase curta acima do título.',
  },
  {
    id: 'titulo',
    role: 'title' as const,
    label: 'Título',
    acceptedTypes: ['ui.title'],
    required: true,
  },
  {
    id: 'descricao',
    role: 'description' as const,
    label: 'Texto de apoio',
    acceptedTypes: ['ui.paragraph'],
    required: true,
  },
  {
    id: 'acoes',
    role: 'primaryAction' as const,
    label: 'Botões',
    acceptedTypes: ['ui.buttons'],
    required: true,
  },
];

const composicoes: StudioCompositionDefinition[] = [
  // ===========================================================================
  // 1 — SaaS: capa cinematográfica com painel do produto
  // ===========================================================================
  {
    key: 'composition.saas.capa-cinematografica',
    label: 'SaaS · capa cinematográfica',
    description: 'Fundo escuro com grade, produto em moldura e prova social.',
    family: 'saas',
    category: 'composicoes',
    preview: 'hero-split',
    outputs: WEB,
    order: 1,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail vira texto e imagem empilhados, sem movimento.',
    performanceCost: 'medium',
    responsiveBehavior: 'No celular o produto vem depois do texto, em coluna única.',
    slots: [
      ...ESPACOS_DE_ABERTURA,
      {
        id: 'visual',
        role: 'visual',
        label: 'Tela do produto',
        acceptedTypes: ['ui.device', 'ui.image'],
        preferredRatio: '16/10',
        required: true,
      },
      {
        id: 'prova',
        role: 'socialProof',
        label: 'Prova social',
        acceptedTypes: ['ui.avatars', 'ui.logos'],
      },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundGradient: 'mesh-dark',
          pattern: 'grid',
          glow: 'top-right',
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          textColor: '#f8fafc',
          motion: 'fade',
        },
        [
          b('layout.section', { padding: '0px' }, [
            s('chapeu', 'ui.badge', {
              text: 'Plataforma completa de operação',
              variant: 'glass',
              motion: 'fade-up',
            }),
            s('titulo', 'ui.title', {
              text: 'Sua operação inteira em uma tela só',
              highlight: 'uma tela só',
              size: 'display',
              level: 'h1',
              accentColor: '#38bdf8',
              motion: 'fade-up',
              motionDelay: '100',
            }),
            s('descricao', 'ui.paragraph', {
              text: 'Vendas, atendimento e cobrança acontecendo no mesmo lugar, em tempo real, sem planilha.',
              size: 'lg',
              maxWidth: '560px',
              motion: 'fade-up',
              motionDelay: '200',
            }),
            s('acoes', 'ui.buttons', {
              items: 'Começar agora|#formulario|solid\nVer demonstração|#demo|glass',
              size: 'lg',
              motion: 'fade-up',
              motionDelay: '300',
            }),
            s('prova', 'ui.avatars', {
              items: AVATARES,
              text: '+2.000 empresas usando todo dia',
              note: 'Nota 4,9 de 5',
            }),
          ]),
          s('visual', 'ui.device', {
            src: FOTO.painel,
            alt: 'Painel do sistema',
            device: 'browser',
            shadow: 'glow',
            motion: 'fade-up',
            motionTrigger: 'load',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 2 — SaaS: recursos em mosaico
  // ===========================================================================
  {
    key: 'composition.saas.mosaico-recursos',
    label: 'SaaS · mosaico de recursos',
    description: 'Cartões de vidro em mosaico sobre fundo escuro.',
    family: 'saas',
    category: 'composicoes',
    preview: 'grid-3',
    outputs: WEB,
    order: 2,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail os cartões viram lista simples.',
    performanceCost: 'medium',
    responsiveBehavior: 'Três colunas no computador, uma no celular.',
    slots: [
      { id: 'titulo', role: 'title', label: 'Título', acceptedTypes: ['ui.title'], required: true },
      {
        id: 'descricao',
        role: 'description',
        label: 'Texto de apoio',
        acceptedTypes: ['ui.paragraph'],
      },
      {
        id: 'lista',
        role: 'list',
        label: 'Cartões',
        acceptedTypes: ['layout.columns'],
        required: true,
      },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundColor: '#0b1120',
          pattern: 'dots',
          paddingY: 'lg',
          align: 'center',
          textColor: '#e2e8f0',
          maxWidth: '1120px',
        },
        [
          s('titulo', 'ui.title', {
            text: 'Tudo que o time precisa, sem trocar de sistema',
            size: 'xl',
            level: 'h2',
            align: 'center',
            accentColor: '#38bdf8',
            motion: 'fade-up',
          }),
          s('descricao', 'ui.paragraph', {
            text: 'Recursos que trabalham juntos desde o primeiro dia.',
            align: 'center',
            maxWidth: '620px',
            motion: 'fade-up',
          }),
          s(
            'lista',
            'layout.columns',
            { count: '3', gap: '20px', alignItems: 'stretch' },
            [
              b('ui.card', {
                icon: '⚡',
                title: 'Automação de ponta a ponta',
                text: 'Processos que seguem sozinhos, com registro de cada passo.',
                variant: 'glass',
                radius: 'xl',
                motion: 'fade-up',
                motionTrigger: 'scroll',
              }),
              b('ui.card', {
                icon: '📊',
                title: 'Números em tempo real',
                text: 'Painéis prontos para acompanhar meta, funil e cobrança.',
                variant: 'glass',
                radius: 'xl',
                motion: 'fade-up',
                motionTrigger: 'scroll',
                motionDelay: '100',
              }),
              b('ui.card', {
                icon: '🔒',
                title: 'Segurança por padrão',
                text: 'Cada empresa vê apenas os próprios dados, com registro de acesso.',
                variant: 'glass',
                radius: 'xl',
                motion: 'fade-up',
                motionTrigger: 'scroll',
                motionDelay: '200',
              }),
            ],
          ),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 3 — SaaS: preços em destaque
  // ===========================================================================
  {
    key: 'composition.saas.precos-destaque',
    label: 'SaaS · planos em destaque',
    description: 'Planos lado a lado com observação de cobrança.',
    family: 'saas',
    category: 'composicoes',
    preview: 'pricing',
    outputs: WEB,
    order: 3,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail os planos viram tabela simples.',
    performanceCost: 'low',
    responsiveBehavior: 'Três colunas no computador, empilhado no celular.',
    slots: [
      { id: 'titulo', role: 'title', label: 'Título', acceptedTypes: ['ui.title'], required: true },
      {
        id: 'lista',
        role: 'list',
        label: 'Planos',
        acceptedTypes: ['page.pricing'],
        required: true,
      },
      { id: 'acoes', role: 'primaryAction', label: 'Botão', acceptedTypes: ['ui.buttons'] },
    ],
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#f8fafc', paddingY: 'lg', align: 'center', maxWidth: '1120px' },
        [
          s('titulo', 'ui.title', {
            text: 'Planos que acompanham o seu crescimento',
            size: 'xl',
            level: 'h2',
            align: 'center',
            motion: 'fade-up',
          }),
          s('lista', 'page.pricing', {
            title: '',
            columns: '3',
            items:
              'Essencial|R$ 149/mês|Até 3 usuários\nCadastro de clientes\nPropostas ilimitadas\nProfissional|R$ 349/mês|Até 10 usuários\nAutomações\nPainéis de resultado\nEmpresarial|Sob consulta|Usuários ilimitados\nIntegrações dedicadas\nSuporte prioritário',
            note: 'Sem fidelidade. Cancele quando quiser.',
          }),
          s('acoes', 'ui.buttons', {
            items: 'Falar com vendas|#formulario|solid',
            align: 'center',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 4 — Luxo: capa editorial
  // ===========================================================================
  {
    key: 'composition.luxo.capa-editorial',
    label: 'Luxo · capa editorial',
    description: 'Fotografia em tela cheia, título sereno e uma única ação.',
    family: 'luxo',
    category: 'composicoes',
    preview: 'hero-image',
    outputs: WEB,
    order: 4,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail vira foto no topo com texto abaixo.',
    performanceCost: 'low',
    responsiveBehavior: 'A foto ocupa a tela inteira em qualquer tamanho.',
    slots: [
      ...ESPACOS_DE_ABERTURA,
      {
        id: 'visual',
        role: 'visual',
        label: 'Fotografia de fundo',
        acceptedTypes: ['section.layered'],
        preferredRatio: '21/9',
        hint: 'A própria faixa carrega a fotografia.',
      },
    ],
    build: () => [
      s(
        'visual',
        'section.layered',
        {
          backgroundImage: FOTO.arquitetura,
          overlay: 'darker',
          minHeight: 'screen',
          paddingY: 'xl',
          align: 'center',
          textColor: '#ffffff',
          maxWidth: '880px',
          motion: 'fade',
        },
        [
          s('chapeu', 'ui.badge', {
            text: 'Desde 1998',
            variant: 'outline',
            align: 'center',
          }),
          s('titulo', 'ui.title', {
            text: 'O cuidado que se percebe em cada detalhe',
            size: 'display',
            level: 'h1',
            align: 'center',
            motion: 'blur-in',
          }),
          s('descricao', 'ui.paragraph', {
            text: 'Projetos sob medida para quem entende que o resultado começa muito antes da entrega.',
            size: 'lg',
            align: 'center',
            maxWidth: '640px',
            motion: 'fade-up',
            motionDelay: '200',
          }),
          s('acoes', 'ui.buttons', {
            items: 'Conhecer o estúdio|#sobre|outline',
            align: 'center',
            size: 'lg',
            motion: 'fade-up',
            motionDelay: '400',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 5 — Luxo: manifesto com foto ao lado
  // ===========================================================================
  {
    key: 'composition.luxo.manifesto',
    label: 'Luxo · manifesto com foto',
    description: 'Texto longo à esquerda e fotografia em retrato à direita.',
    family: 'luxo',
    category: 'composicoes',
    preview: 'split-right',
    outputs: WEB,
    order: 5,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail o texto vem antes da foto.',
    performanceCost: 'low',
    responsiveBehavior: 'Duas colunas no computador, empilhado no celular.',
    slots: [
      { id: 'chapeu', role: 'eyebrow', label: 'Chapéu', acceptedTypes: ['ui.badge'] },
      { id: 'titulo', role: 'title', label: 'Título', acceptedTypes: ['ui.title'], required: true },
      {
        id: 'descricao',
        role: 'description',
        label: 'Texto',
        acceptedTypes: ['ui.paragraph'],
        required: true,
      },
      {
        id: 'visual',
        role: 'visual',
        label: 'Fotografia',
        acceptedTypes: ['ui.image'],
        preferredRatio: '3/4',
        required: true,
      },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundColor: '#faf7f2',
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          textColor: '#1c1917',
          maxWidth: '1120px',
        },
        [
          b('layout.section', { padding: '0px' }, [
            s('chapeu', 'ui.badge', { text: 'Nosso jeito de trabalhar', variant: 'outline' }),
            s('titulo', 'ui.title', {
              text: 'Menos pressa, mais presença',
              size: 'xl',
              level: 'h2',
              motion: 'fade-up',
            }),
            s('descricao', 'ui.paragraph', {
              text: 'Cada projeto começa com uma conversa longa e termina com um acompanhamento que não acaba na entrega. É assim há mais de vinte anos.',
              size: 'lg',
              maxWidth: '520px',
              motion: 'fade-up',
            }),
          ]),
          s('visual', 'ui.image', {
            src: FOTO.interior,
            alt: 'Ambiente do estúdio',
            ratio: 'portrait',
            radius: 'lg',
            shadow: 'lg',
            mask: 'arch',
            motion: 'reveal',
            motionTrigger: 'scroll',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 6 — Luxo: depoimento em destaque
  // ===========================================================================
  {
    key: 'composition.luxo.depoimento-destaque',
    label: 'Luxo · depoimento em destaque',
    description: 'Uma única fala de cliente, com muito espaço em volta.',
    family: 'luxo',
    category: 'composicoes',
    preview: 'text',
    outputs: WEB,
    order: 6,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail vira citação simples.',
    performanceCost: 'low',
    responsiveBehavior: 'Mesma leitura em qualquer tamanho de tela.',
    slots: [
      {
        id: 'prova',
        role: 'socialProof',
        label: 'Depoimento',
        acceptedTypes: ['page.testimonials', 'ui.paragraph'],
        required: true,
      },
      { id: 'chapeu', role: 'eyebrow', label: 'Chapéu', acceptedTypes: ['ui.badge'] },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundColor: '#1c1917',
          paddingY: 'xl',
          align: 'center',
          textColor: '#fafaf9',
          maxWidth: '880px',
        },
        [
          s('chapeu', 'ui.badge', { text: 'Quem já viveu isso', variant: 'outline', align: 'center' }),
          s('prova', 'ui.paragraph', {
            text: '“Foi a primeira vez que sentimos que alguém entendeu o nosso negócio antes de propor qualquer coisa.”',
            size: 'lg',
            align: 'center',
            maxWidth: '720px',
            motion: 'fade-up',
            motionTrigger: 'scroll',
          }),
          b('ui.avatars', {
            items: AVATARES,
            text: 'Marina Alves · diretora de operações',
            align: 'center',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 7 — Evento: capa do palestrante
  // ===========================================================================
  {
    key: 'composition.evento.capa-palestrante',
    label: 'Evento · capa do palestrante',
    description: 'Cor forte, data em destaque e foto de palco.',
    family: 'evento',
    category: 'composicoes',
    preview: 'hero-split',
    outputs: WEB,
    order: 7,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail vira cartaz estático.',
    performanceCost: 'medium',
    responsiveBehavior: 'A foto vem depois do texto no celular.',
    slots: [
      ...ESPACOS_DE_ABERTURA,
      {
        id: 'visual',
        role: 'visual',
        label: 'Foto do palco',
        acceptedTypes: ['ui.image'],
        preferredRatio: '4/5',
        required: true,
      },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundGradient: 'sunset',
          pattern: 'rays',
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          textColor: '#ffffff',
          allowLayers: true,
        },
        [
          b('layout.section', { padding: '0px' }, [
            s('chapeu', 'ui.badge', {
              text: '18 de outubro · São Paulo',
              variant: 'solid',
              motion: 'fade-down',
            }),
            s('titulo', 'ui.title', {
              text: 'Um dia inteiro sobre o que realmente faz vender',
              highlight: 'faz vender',
              size: 'display',
              level: 'h1',
              motion: 'fade-up',
            }),
            s('descricao', 'ui.paragraph', {
              text: 'Oito conversas curtas, sem teoria solta, com quem coloca a mão na operação todo dia.',
              size: 'lg',
              maxWidth: '540px',
              motion: 'fade-up',
              motionDelay: '100',
            }),
            s('acoes', 'ui.buttons', {
              items: 'Garantir meu ingresso|#ingressos|solid\nVer programação|#programacao|glass',
              size: 'lg',
              motion: 'fade-up',
              motionDelay: '200',
            }),
          ]),
          s('visual', 'ui.image', {
            src: FOTO.palestra,
            alt: 'Palestra no palco',
            ratio: 'portrait',
            radius: 'xl',
            shadow: 'glow',
            tilt: 'left',
            motion: 'scale',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 8 — Evento: programação em etapas
  // ===========================================================================
  {
    key: 'composition.evento.programacao',
    label: 'Evento · programação do dia',
    description: 'Horários em sequência, com destaque de cor.',
    family: 'evento',
    category: 'composicoes',
    preview: 'steps',
    outputs: WEB,
    order: 8,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail vira lista numerada.',
    performanceCost: 'low',
    responsiveBehavior: 'Sequência única em qualquer tela.',
    slots: [
      { id: 'titulo', role: 'title', label: 'Título', acceptedTypes: ['ui.title'], required: true },
      {
        id: 'lista',
        role: 'list',
        label: 'Programação',
        acceptedTypes: ['page.steps'],
        required: true,
      },
    ],
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#fff7ed', paddingY: 'lg', align: 'center', maxWidth: '960px' },
        [
          s('titulo', 'ui.title', {
            text: 'Como vai ser o dia',
            size: 'xl',
            level: 'h2',
            align: 'center',
            accentColor: '#ea580c',
            motion: 'fade-up',
          }),
          s('lista', 'page.steps', {
            title: '',
            items:
              '09h00|Credenciamento e café\n10h00|Abertura: o que mudou na venda em 2026\n11h30|Painel: operação enxuta de verdade\n14h00|Oficina prática de atendimento\n16h30|Encerramento e networking',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 9 — Evento: chamada com inscrição
  // ===========================================================================
  {
    key: 'composition.evento.inscricao',
    label: 'Evento · chamada com inscrição',
    description: 'Convite direto com formulário ao lado.',
    family: 'evento',
    category: 'composicoes',
    preview: 'form',
    outputs: WEB,
    order: 9,
    version: 1,
    supportedRenderModes: {
      editor: 'full',
      published: 'full',
      thumbnail: 'static',
      pdf: 'none',
      email: 'none',
    },
    fallback: 'Em PDF e e-mail o formulário é substituído por um convite com link.',
    performanceCost: 'low',
    responsiveBehavior: 'Formulário abaixo do texto no celular.',
    slots: [
      { id: 'titulo', role: 'title', label: 'Título', acceptedTypes: ['ui.title'], required: true },
      {
        id: 'descricao',
        role: 'description',
        label: 'Texto de apoio',
        acceptedTypes: ['ui.paragraph'],
        required: true,
      },
      { id: 'form', role: 'form', label: 'Formulário', acceptedTypes: ['page.form'], required: true },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundGradient: 'aurora',
          layout: 'split',
          paddingY: 'lg',
          align: 'left',
          textColor: '#0f172a',
          maxWidth: '1080px',
        },
        [
          b('layout.section', { padding: '0px' }, [
            s('titulo', 'ui.title', {
              text: 'As vagas acabam antes do dia',
              size: 'xl',
              level: 'h2',
              motion: 'fade-up',
            }),
            s('descricao', 'ui.paragraph', {
              text: 'Deixe seu contato e a gente segura o seu lugar por 48 horas.',
              size: 'lg',
              maxWidth: '460px',
              motion: 'fade-up',
            }),
          ]),
          s('form', 'page.form', {
            title: 'Quero meu lugar',
            description: 'Respondemos em poucos minutos.',
            submitLabel: 'Garantir vaga',
            successType: 'message',
            successTitle: 'Lugar reservado!',
            successMessage: 'Enviamos os próximos passos para o seu e-mail.',
          }),
        ],
      ),
    ],
  },

  // ===========================================================================
  // 10 — Rodapé cinematográfico (serve às três famílias)
  // ===========================================================================
  {
    key: 'composition.saas.rodape-cinematografico',
    label: 'Fechamento · rodapé cinematográfico',
    description: 'Última chamada em faixa escura, com rodapé logo abaixo.',
    family: 'saas',
    category: 'composicoes',
    preview: 'footer',
    outputs: WEB,
    order: 10,
    version: 1,
    supportedRenderModes: MODOS,
    fallback: 'Em PDF e e-mail vira bloco de contato simples.',
    performanceCost: 'low',
    responsiveBehavior: 'Empilha no celular, mantendo o botão visível.',
    slots: [
      { id: 'titulo', role: 'title', label: 'Última chamada', acceptedTypes: ['ui.title'], required: true },
      { id: 'acoes', role: 'primaryAction', label: 'Botão', acceptedTypes: ['ui.buttons'], required: true },
      { id: 'rodape', role: 'list', label: 'Rodapé', acceptedTypes: ['page.footer'], required: true },
    ],
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.cidadeNoite,
          overlay: 'darker',
          paddingY: 'xl',
          align: 'center',
          textColor: '#ffffff',
          maxWidth: '880px',
        },
        [
          s('titulo', 'ui.title', {
            text: 'Vamos começar pelo que dá resultado mais rápido',
            size: 'xl',
            level: 'h2',
            align: 'center',
            motion: 'fade-up',
            motionTrigger: 'scroll',
          }),
          s('acoes', 'ui.buttons', {
            items: 'Falar agora|#formulario|solid',
            align: 'center',
            size: 'lg',
            motion: 'fade-up',
            motionTrigger: 'scroll',
          }),
        ],
      ),
      s('rodape', 'page.footer', {
        brand: 'Sua Empresa',
        text: 'Atendimento de segunda a sexta, das 9h às 18h.',
        links: 'Início|#topo\nPlanos|#planos\nContato|#formulario',
        legal: '© 2026 Sua Empresa. Todos os direitos reservados.',
        backgroundColor: '#0b1120',
        textColor: '#cbd5f5',
      }),
    ],
  },
];

registerCompositions(composicoes);

// A biblioteca lateral não precisa de tela nova: a categoria "Composições"
// entra no mesmo menu dos blocos prontos e a inserção continua a mesma.
registerPatternCategory({
  key: 'composicoes',
  label: 'Composições',
  icon: 'layout-template',
  order: 0,
  description: 'Trechos de página inteiros, com espaços já organizados.',
});

composicoes.forEach((composicao) => {
  registerPattern({
    key: composicao.key,
    category: composicao.category,
    label: composicao.label,
    description: composicao.description,
    preview: composicao.preview,
    outputs: composicao.outputs,
    templateTypes: composicao.templateTypes,
    order: composicao.order,
    build: () => expandComposition(composicao.key),
  });
});
